<template>
  <div class="kol-tl">
    <div class="kol-tl__toolbar">
      <a class="kol-tl__nav-button" @click="goToday()">
        {{ $t('keyoflifeViews.today') }}
      </a>
      <div class="kol-tl__search">
        <i class="iconoir-search kol-tl__search-icon"></i>
        <input
          v-model="searchQuery"
          type="text"
          class="kol-tl__search-input"
          :placeholder="$t('keyoflifeViews.searchPlaceholder')"
        />
        <a
          v-if="searchQuery"
          class="kol-tl__search-clear"
          @click="searchQuery = ''"
        >
          <i class="iconoir-cancel"></i>
        </a>
      </div>
      <div class="kol-tl__toolbar-right">
        <span v-if="truncated" class="kol-tl__notice">
          {{ $t('keyoflifeViews.truncated', { count: rows.length }) }}
        </span>
        <Dropdown
          :value="view.start_date_field"
          :show-search="false"
          :placeholder="$t('keyoflifeViews.chooseStartField')"
          class="kol-tl__field-dropdown"
          :disabled="readOnly"
          @input="updateField('start_date_field', $event)"
        >
          <DropdownItem
            v-for="field in dateFields"
            :key="field.id"
            :name="field.name"
            :value="field.id"
            icon="iconoir-calendar"
          ></DropdownItem>
        </Dropdown>
        <Dropdown
          :value="view.end_date_field"
          :show-search="false"
          :placeholder="$t('keyoflifeViews.chooseEndDateField')"
          class="kol-tl__field-dropdown"
          :disabled="readOnly"
          @input="updateField('end_date_field', $event)"
        >
          <DropdownItem
            :name="$t('keyoflifeViews.noEndDate')"
            :value="null"
            icon="iconoir-circle"
          ></DropdownItem>
          <DropdownItem
            v-for="field in dateFields"
            :key="field.id"
            :name="field.name"
            :value="field.id"
            icon="iconoir-calendar"
          ></DropdownItem>
        </Dropdown>
        <Dropdown
          :value="view.color_field"
          :show-search="false"
          :placeholder="$t('keyoflifeViews.chooseColorField')"
          class="kol-tl__field-dropdown"
          :disabled="readOnly"
          @input="updateField('color_field', $event)"
        >
          <DropdownItem
            :name="$t('keyoflifeViews.noColor')"
            :value="null"
            icon="iconoir-circle"
          ></DropdownItem>
          <DropdownItem
            v-for="field in singleSelectFields"
            :key="field.id"
            :name="field.name"
            :value="field.id"
            icon="iconoir-fill-color"
          ></DropdownItem>
        </Dropdown>
        <Dropdown
          :value="view.progress_field"
          :show-search="false"
          :placeholder="$t('keyoflifeViews.chooseProgressField')"
          class="kol-tl__field-dropdown"
          :disabled="readOnly"
          @input="updateField('progress_field', $event)"
        >
          <DropdownItem
            :name="$t('keyoflifeViews.noProgress')"
            :value="null"
            icon="iconoir-circle"
          ></DropdownItem>
          <DropdownItem
            v-for="field in numberFields"
            :key="field.id"
            :name="field.name"
            :value="field.id"
            icon="iconoir-percentage"
          ></DropdownItem>
        </Dropdown>
        <Dropdown
          :value="view.dependency_field"
          :show-search="false"
          :placeholder="$t('keyoflifeViews.chooseDependencyField')"
          class="kol-tl__field-dropdown"
          :disabled="readOnly"
          @input="updateField('dependency_field', $event)"
        >
          <DropdownItem
            :name="$t('keyoflifeViews.noDependency')"
            :value="null"
            icon="iconoir-circle"
          ></DropdownItem>
          <DropdownItem
            v-for="field in linkRowFields"
            :key="field.id"
            :name="field.name"
            :value="field.id"
            icon="iconoir-link"
          ></DropdownItem>
        </Dropdown>
        <Dropdown
          :value="view.group_field"
          :show-search="false"
          :placeholder="$t('keyoflifeViews.chooseGroupField')"
          class="kol-tl__field-dropdown"
          :disabled="readOnly"
          @input="updateField('group_field', $event)"
        >
          <DropdownItem
            :name="$t('keyoflifeViews.noGroup')"
            :value="null"
            icon="iconoir-circle"
          ></DropdownItem>
          <DropdownItem
            v-for="field in singleSelectFields"
            :key="field.id"
            :name="field.name"
            :value="field.id"
            icon="iconoir-list"
          ></DropdownItem>
        </Dropdown>
        <div
          class="kol-tl__mstyle"
          :title="$t('keyoflifeViews.milestoneStyle')"
        >
          <a
            class="kol-tl__mstyle-btn"
            :class="{
              'kol-tl__mstyle-btn--active': milestoneStyle === 'diamond',
            }"
            @click="updateField('milestone_style', 'diamond')"
          >
            <i class="iconoir-rhombus"></i>
          </a>
          <a
            class="kol-tl__mstyle-btn"
            :class="{ 'kol-tl__mstyle-btn--active': milestoneStyle === 'flag' }"
            @click="updateField('milestone_style', 'flag')"
          >
            <i class="iconoir-flag"></i>
          </a>
        </div>
        <a
          class="kol-tl__nav-button"
          :title="$t('keyoflifeViews.refresh')"
          @click="fetchRows()"
        >
          <i class="iconoir-refresh-double"></i>
        </a>
      </div>
    </div>

    <div v-if="!startField" class="kol-tl__empty">
      <i class="iconoir-timeline kol-tl__empty-icon"></i>
      <div class="kol-tl__empty-text">
        {{ $t('keyoflifeViews.noStartFieldTitle') }}
      </div>
      <div class="kol-tl__empty-subtext">
        {{
          dateFields.length > 0
            ? $t('keyoflifeViews.noStartFieldHelp')
            : $t('keyoflifeViews.noDateFieldsInTable')
        }}
      </div>
    </div>

    <div v-else ref="scroll" class="kol-tl__scroll">
      <div v-if="loading" class="loading-absolute-center"></div>
      <div class="kol-tl__inner" :style="{ width: labelWidth + axisWidth + 'px' }">
        <!-- Naglowek: miesiace + dni -->
        <div class="kol-tl__header">
          <div
            class="kol-tl__corner"
            :style="{ width: labelWidth + 'px' }"
          >
            {{ $t('keyoflifeViews.timelineRowsHeader') }}
          </div>
          <div class="kol-tl__axis" :style="{ width: axisWidth + 'px' }">
            <div class="kol-tl__months">
              <div
                v-for="m in months"
                :key="m.key"
                class="kol-tl__month"
                :style="{ width: m.days * dayWidth + 'px' }"
              >
                {{ m.label }}
              </div>
            </div>
            <div class="kol-tl__days">
              <div
                v-for="d in days"
                :key="d.key"
                class="kol-tl__day"
                :class="{
                  'kol-tl__day--weekend': d.isWeekend,
                  'kol-tl__day--today': d.isToday,
                }"
                :style="{ width: dayWidth + 'px' }"
              >
                {{ d.dom }}
              </div>
            </div>
          </div>
        </div>

        <!-- Wiersze -->
        <div class="kol-tl__rows">
          <!-- Nakladka SVG ze strzalkami zaleznosci -->
          <svg
            v-if="dependencyField && depArrows.length"
            class="kol-tl__deps"
            :style="{
              left: labelWidth + 'px',
              width: axisWidth + 'px',
              height: renderItems.length * rowHeight + 'px',
            }"
            :width="axisWidth"
            :height="renderItems.length * rowHeight"
          >
            <defs>
              <marker
                id="kol-tl-arrow"
                markerWidth="6"
                markerHeight="6"
                refX="5"
                refY="3"
                orient="auto"
              >
                <path d="M0,0 L6,3 L0,6 z" class="kol-tl__dep-head" />
              </marker>
            </defs>
            <path
              v-for="a in depArrows"
              :key="a.key"
              :d="a.path"
              class="kol-tl__dep-path"
              marker-end="url(#kol-tl-arrow)"
            />
          </svg>
          <template v-for="item in renderItems">
            <!-- Naglowek toru (swimlane) -->
            <div
              v-if="item.kind === 'header'"
              :key="'h-' + item.key"
              class="kol-tl__group"
            >
              <div
                class="kol-tl__group-label"
                :style="{ width: labelWidth + 'px' }"
              >
                <span
                  v-if="item.color"
                  class="kol-tl__group-dot"
                  :class="'background-color--' + item.color"
                ></span>
                <span class="kol-tl__group-name">{{ item.name }}</span>
                <span class="kol-tl__group-count">{{ item.count }}</span>
              </div>
              <div
                class="kol-tl__group-fill"
                :style="{ width: axisWidth + 'px' }"
              ></div>
            </div>
            <!-- Wiersz zadania -->
            <div v-else :key="item.bar.row.id" class="kol-tl__row">
              <div
                class="kol-tl__label"
                :style="{ width: labelWidth + 'px' }"
                :title="item.bar.title"
                @click="openRow(item.bar.row)"
              >
                {{ item.bar.title }}
              </div>
              <div class="kol-tl__track" :style="{ width: axisWidth + 'px' }">
                <div
                  v-if="item.bar.isMilestone"
                  class="kol-tl__milestone-wrap"
                  :style="{ left: item.bar.centerPx + 'px' }"
                  @click="openRow(item.bar.row)"
                >
                  <span
                    class="kol-tl__milestone"
                    :class="[
                      item.bar.color
                        ? 'background-color--' + item.bar.color
                        : '',
                      milestoneStyle === 'flag'
                        ? 'kol-tl__milestone--flag'
                        : 'kol-tl__milestone--diamond',
                    ]"
                  ></span>
                  <span class="kol-tl__milestone-label">{{
                    item.bar.title
                  }}</span>
                </div>
                <a
                  v-else
                  class="kol-tl__bar"
                  :class="[
                    item.bar.color
                      ? 'background-color--' + item.bar.color
                      : '',
                    { 'kol-tl__bar--colored': item.bar.color },
                  ]"
                  :style="{
                    left: item.bar.leftPx + 'px',
                    width: item.bar.widthPx + 'px',
                  }"
                  @click="openRow(item.bar.row)"
                >
                  <span
                    v-if="item.bar.progress !== null"
                    class="kol-tl__bar-fill"
                    :style="{ width: item.bar.progress + '%' }"
                    :title="item.bar.progress + '%'"
                  ></span>
                  <span class="kol-tl__bar-label">{{ item.bar.title }}</span>
                </a>
              </div>
            </div>
          </template>
          <div v-if="bars.length === 0" class="kol-tl__norows">
            {{ $t('keyoflifeViews.timelineNoRows') }}
          </div>
        </div>
      </div>
    </div>

    <RowEditModal
      ref="rowEditModal"
      :database="database"
      :table="table"
      :view="view"
      :all-fields-in-table="fields"
      :primary-is-sortable="false"
      :visible-fields="fields"
      :hidden-fields="[]"
      :rows="rows"
      :read-only="!canManageRows"
      :show-hidden-fields="false"
      @update="updateValue"
      @refresh-row="refreshRow"
      @field-updated="$emit('refresh', $event)"
      @field-deleted="$emit('refresh')"
      @field-created="$emit('refresh')"
    ></RowEditModal>
  </div>
</template>

<script>
import RowEditModal from '@baserow/modules/database/components/row/RowEditModal'
import RowService from '@baserow/modules/database/services/row'
import { notifyIf } from '@baserow/modules/core/utils/error'

import TimelineService from '@keyoflife-views/services/timeline'

const DAY_MS = 86400000
const DAY_WIDTH = 36
const LABEL_WIDTH = 200
const ROW_HEIGHT = 36
const WINDOW_PADDING_DAYS = 3
const DATE_FIELD_TYPES = ['date', 'created_on', 'last_modified']

export default {
  name: 'KeyOfLifeTimelineView',
  components: { RowEditModal },
  props: {
    fields: { type: Array, required: true },
    view: { type: Object, required: true },
    table: { type: Object, required: true },
    database: { type: Object, required: true },
    readOnly: { type: Boolean, required: true },
    storePrefix: { type: String, required: false, default: '' },
  },
  data() {
    return {
      rows: [],
      totalCount: 0,
      loading: false,
      searchQuery: '',
      fieldOptions: {},
      dayWidth: DAY_WIDTH,
      labelWidth: LABEL_WIDTH,
      rowHeight: ROW_HEIGHT,
    }
  },
  computed: {
    startField() {
      return this.fields.find((f) => f.id === this.view.start_date_field)
    },
    endField() {
      return this.fields.find((f) => f.id === this.view.end_date_field)
    },
    colorField() {
      return this.fields.find((f) => f.id === this.view.color_field)
    },
    dateFields() {
      return this.fields.filter(
        (f) =>
          DATE_FIELD_TYPES.includes(f.type) ||
          (f.type === 'formula' && f.formula_type === 'date')
      )
    },
    singleSelectFields() {
      return this.fields.filter((f) => f.type === 'single_select')
    },
    numberFields() {
      return this.fields.filter(
        (f) =>
          f.type === 'number' ||
          f.type === 'rating' ||
          (f.type === 'formula' && f.formula_type === 'number')
      )
    },
    progressField() {
      return this.fields.find((f) => f.id === this.view.progress_field)
    },
    linkRowFields() {
      return this.fields.filter((f) => f.type === 'link_row')
    },
    dependencyField() {
      return this.fields.find((f) => f.id === this.view.dependency_field)
    },
    groupField() {
      return this.fields.find((f) => f.id === this.view.group_field)
    },
    milestoneStyle() {
      return this.view.milestone_style === 'flag' ? 'flag' : 'diamond'
    },
    // Lista renderowania: gdy brak grupowania = same paski; gdy jest =
    // naglowek toru + jego paski (swimlanes). Kolejnosc grup wg opcji.
    renderItems() {
      if (!this.groupField) {
        return this.bars.map((bar) => ({ kind: 'bar', bar }))
      }
      const gKey = `field_${this.groupField.id}`
      const byGroup = {}
      for (const bar of this.bars) {
        const v = bar.row[gKey]
        const k = v && v.id ? String(v.id) : '__none__'
        if (!byGroup[k]) {
          byGroup[k] = []
        }
        byGroup[k].push(bar)
      }
      const order = [
        { key: '__none__', name: this.$t('keyoflifeViews.noGroup'), color: null },
        ...(this.groupField.select_options || []).map((o) => ({
          key: String(o.id),
          name: o.value,
          color: o.color,
        })),
      ]
      const items = []
      for (const g of order) {
        const groupBars = byGroup[g.key]
        if (!groupBars || groupBars.length === 0) {
          continue
        }
        items.push({
          kind: 'header',
          key: g.key,
          name: g.name,
          color: g.color,
          count: groupBars.length,
        })
        for (const bar of groupBars) {
          items.push({ kind: 'bar', bar })
        }
      }
      return items
    },
    // Mapa row.id -> srodek Y (px) wg pozycji w renderItems (z naglowkami).
    barCenterY() {
      const map = {}
      this.renderItems.forEach((item, index) => {
        if (item.kind === 'bar') {
          map[item.bar.row.id] = index * this.rowHeight + this.rowHeight / 2
        }
      })
      return map
    },
    // Strzalki zaleznosci: od konca poprzednika do poczatku nastepnika.
    depArrows() {
      if (!this.dependencyField) {
        return []
      }
      const barById = {}
      this.bars.forEach((bar) => {
        barById[bar.row.id] = bar
      })
      const yById = this.barCenterY
      const depKey = `field_${this.dependencyField.id}`
      const gap = 8
      const arrows = []
      for (const bar of this.bars) {
        const links = bar.row[depKey]
        if (!Array.isArray(links)) {
          continue
        }
        const y2 = yById[bar.row.id]
        const x2 = bar.isMilestone ? bar.centerPx : bar.leftPx
        for (const link of links) {
          const pred = barById[link.id]
          if (!pred || yById[link.id] === undefined) {
            continue
          }
          const x1 = pred.isMilestone
            ? pred.centerPx
            : pred.leftPx + pred.widthPx
          const y1 = yById[link.id]
          const midX = Math.max(x1 + gap, x2 - gap)
          arrows.push({
            key: `${pred.row.id}->${bar.row.id}`,
            path: `M ${x1} ${y1} H ${midX} V ${y2} H ${x2}`,
          })
        }
      }
      return arrows
    },
    primaryField() {
      return this.fields.find((f) => f.primary)
    },
    canManageRows() {
      return (
        !this.readOnly &&
        this.$hasPermission(
          'database.table.update_row',
          this.table,
          this.database.workspace.id
        )
      )
    },
    truncated() {
      return this.totalCount > this.rows.length
    },
    // Wiersze z data poczatku (po filtrze szukajki) — kazdy = jeden pasek.
    timelineRows() {
      if (!this.startField) {
        return []
      }
      const query = this.searchQuery.trim().toLowerCase()
      const out = []
      for (const row of this.rows) {
        const startKey = this.dayKeyOfField(row, this.startField)
        if (!startKey) {
          continue
        }
        let endKey = this.endField
          ? this.dayKeyOfField(row, this.endField)
          : startKey
        if (!endKey || endKey < startKey) {
          endKey = startKey
        }
        const title = this.rowTitle(row)
        if (query && !title.toLowerCase().includes(query)) {
          continue
        }
        out.push({ row, startKey, endKey, title, color: this.rowColor(row) })
      }
      out.sort((a, b) => a.startKey.localeCompare(b.startKey))
      return out
    },
    // Okno osi: min poczatek .. max koniec (z paddingiem); domyslnie biezacy
    // miesiac gdy brak danych.
    windowRange() {
      const keys = []
      for (const e of this.timelineRows) {
        keys.push(e.startKey, e.endKey)
      }
      let min
      let max
      if (keys.length === 0) {
        const now = new Date()
        min = new Date(Date.UTC(now.getFullYear(), now.getMonth(), 1))
        max = new Date(Date.UTC(now.getFullYear(), now.getMonth() + 1, 0))
      } else {
        keys.sort()
        min = this.keyToUtc(keys[0])
        max = this.keyToUtc(keys[keys.length - 1])
      }
      min = new Date(min.getTime() - WINDOW_PADDING_DAYS * DAY_MS)
      max = new Date(max.getTime() + WINDOW_PADDING_DAYS * DAY_MS)
      const totalDays = Math.round((max - min) / DAY_MS) + 1
      return { start: min, days: totalDays }
    },
    axisWidth() {
      return this.windowRange.days * this.dayWidth
    },
    days() {
      const out = []
      const todayKey = this.utcKey(new Date())
      for (let i = 0; i < this.windowRange.days; i++) {
        const d = new Date(this.windowRange.start.getTime() + i * DAY_MS)
        const dow = d.getUTCDay()
        const key = this.utcKey(d)
        out.push({
          key,
          dom: d.getUTCDate(),
          isWeekend: dow === 0 || dow === 6,
          isToday: key === todayKey,
        })
      }
      return out
    },
    months() {
      const out = []
      let current = null
      for (const d of this.days) {
        const [y, m] = d.key.split('-')
        const mk = `${y}-${m}`
        if (!current || current.key !== mk) {
          const date = this.keyToUtc(d.key)
          const label = new Intl.DateTimeFormat(this.$i18n.locale, {
            month: 'long',
            year: 'numeric',
            timeZone: 'UTC',
          }).format(date)
          current = {
            key: mk,
            label: label.charAt(0).toUpperCase() + label.slice(1),
            days: 0,
          }
          out.push(current)
        }
        current.days++
      }
      return out
    },
    bars() {
      const startMs = this.windowRange.start.getTime()
      return this.timelineRows.map((e) => {
        const sIdx = Math.round((this.keyToUtc(e.startKey) - startMs) / DAY_MS)
        const eIdx = Math.round((this.keyToUtc(e.endKey) - startMs) / DAY_MS)
        // Kamien milowy: zerowy czas trwania (brak konca lub start==koniec).
        const isMilestone = e.startKey === e.endKey
        return {
          row: e.row,
          title: e.title,
          color: e.color,
          isMilestone,
          progress: this.rowProgress(e.row),
          leftPx: sIdx * this.dayWidth,
          widthPx: (eIdx - sIdx + 1) * this.dayWidth,
          // Romb wycentrowany na dniu.
          centerPx: sIdx * this.dayWidth + this.dayWidth / 2,
        }
      })
    },
  },
  watch: {
    'view.id'() {
      this.fetchRows()
    },
  },
  mounted() {
    this.fetchRows().then(() => this.$nextTick(() => this.scrollToToday()))
  },
  methods: {
    async fetchRows() {
      this.loading = true
      try {
        const { data } = await TimelineService(this.$client).fetchRows({
          viewId: this.view.id,
        })
        this.rows = data.results
        this.totalCount = data.count
        this.fieldOptions = data.field_options || {}
      } catch (error) {
        notifyIf(error, 'rows')
      }
      this.loading = false
    },
    async updateField(key, fieldId) {
      if (fieldId === this.view[key]) {
        return
      }
      try {
        await this.$store.dispatch('view/update', {
          view: this.view,
          values: { [key]: fieldId },
        })
      } catch (error) {
        notifyIf(error, 'view')
      }
    },
    openRow(row) {
      this.$refs.rowEditModal.show(row.id)
    },
    async updateRowField(row, field, value) {
      const fieldType = this.$registry.get('field', field.type)
      const prepared = fieldType.prepareValueForUpdate(field, value)
      const payload = { [`field_${field.id}`]: prepared }
      const { data } = await RowService(this.$client).update(
        this.table.id,
        row.id,
        payload
      )
      this.replaceRowLocally(data)
      return data
    },
    async updateValue({ row, field, value }) {
      try {
        await this.updateRowField(row, field, value)
      } catch (error) {
        notifyIf(error, 'field')
      }
    },
    replaceRowLocally(updated) {
      const index = this.rows.findIndex(
        (r) => r !== null && r.id === updated.id
      )
      if (index !== -1) {
        this.rows.splice(index, 1, updated)
      } else {
        this.rows.push(updated)
      }
    },
    async refreshRow(row) {
      try {
        const { data } = await RowService(this.$client).get(
          this.table.id,
          row.id
        )
        this.replaceRowLocally(data)
      } catch (error) {
        notifyIf(error, 'row')
      }
    },
    goToday() {
      this.scrollToToday()
    },
    scrollToToday() {
      const scroll = this.$refs.scroll
      if (!scroll) {
        return
      }
      const todayKey = this.utcKey(new Date())
      const idx = this.days.findIndex((d) => d.key === todayKey)
      if (idx >= 0) {
        scroll.scrollLeft = Math.max(
          0,
          this.labelWidth + idx * this.dayWidth - scroll.clientWidth / 2
        )
      }
    },
    // --- helpery dat (operujemy w UTC dla stabilnosci) ---------------------
    dayKeyOfField(row, field) {
      const value = row[`field_${field.id}`]
      if (!value || typeof value !== 'string') {
        return null
      }
      const hasTime =
        field.date_include_time === true ||
        ['created_on', 'last_modified'].includes(field.type)
      if (!hasTime) {
        return value.slice(0, 10)
      }
      const d = new Date(value)
      return isNaN(d.getTime()) ? null : this.utcKey(d)
    },
    keyToUtc(key) {
      const [y, m, d] = key.split('-').map(Number)
      return new Date(Date.UTC(y, m - 1, d))
    },
    utcKey(date) {
      const pad = (n) => String(n).padStart(2, '0')
      return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(
        date.getUTCDate()
      )}`
    },
    rowTitle(row) {
      const field = this.primaryField
      if (!field) {
        return ''
      }
      const fieldType = this.$registry.get('field', field.type)
      const value = fieldType.toHumanReadableString(
        field,
        row[`field_${field.id}`]
      )
      return value || this.$t('keyoflifeViews.unnamed')
    },
    rowColor(row) {
      if (!this.colorField) {
        return null
      }
      const value = row[`field_${this.colorField.id}`]
      return value && value.color ? value.color : null
    },
    // Postep 0-100 z pola liczbowego (null gdy brak pola/wartosci).
    rowProgress(row) {
      if (!this.progressField) {
        return null
      }
      const raw = row[`field_${this.progressField.id}`]
      if (raw === null || raw === undefined || raw === '') {
        return null
      }
      const num = parseFloat(raw)
      if (isNaN(num)) {
        return null
      }
      return Math.max(0, Math.min(100, num))
    },
  },
}
</script>
